import Phaser from 'phaser';
import type { SaveManager } from '@/game/systems/SaveManager';
import type { AudioManager } from '@/game/systems/AudioManager';
import type { SongDifficulty, SongMeta, SongProgress } from '@/types';
import { SONGS } from '@/data/songs';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { PALETTE, bodyTextStyle, createButton, titleTextStyle } from '@/game/ui/theme';

const DIFFICULTY_TIERS: { key: SongDifficulty; label: string; color: number }[] = [
  { key: 'easy', label: 'Easy', color: PALETTE.success },
  { key: 'normal', label: 'Medium', color: PALETTE.secondary },
  { key: 'hard', label: 'Hard', color: PALETTE.danger },
];

const CARD_H = 190;

/** Best-of-three progress (by high score) across a difficulty-variant song's tiers,
 * so the compact card can show one meaningful summary without picking a difficulty
 * to display arbitrarily. */
function getBestProgress(song: SongMeta, saveManager: SaveManager): SongProgress {
  return DIFFICULTY_TIERS.map((tier) => saveManager.getSongProgress(`${song.id}:${tier.key}`)).reduce((best, p) =>
    p.highScore > best.highScore ? p : best
  );
}

export class SongSelectScene extends Phaser.Scene {
  private modalObjects: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super('SongSelectScene');
  }

  create(): void {
    const saveManager = this.game.registry.get('saveManager') as SaveManager;
    const audioManager = this.game.registry.get('audioManager') as AudioManager;

    this.add.rectangle(0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT, PALETTE.bgBottom).setOrigin(0);
    this.add.text(INTERNAL_WIDTH / 2, 70, 'Choose a Song', titleTextStyle(42)).setOrigin(0.5);

    const cardW = INTERNAL_WIDTH - 80;
    const x = INTERNAL_WIDTH / 2;

    SONGS.forEach((song, index) => {
      const y = 150 + index * (CARD_H + 24);

      const card = this.add.graphics();
      card.fillStyle(PALETTE.panel, 1);
      card.fillRoundedRect(x - cardW / 2, y, cardW, CARD_H, 24);
      card.lineStyle(3, PALETTE.panelLight, 1);
      card.strokeRoundedRect(x - cardW / 2, y, cardW, CARD_H, 24);

      this.add.text(x - cardW / 2 + 32, y + 26, song.title, bodyTextStyle(30)).setFontStyle('700');
      this.add
        .text(x - cardW / 2 + 32, y + 68, `${song.artist} • Difficulty ${song.difficulty}/5`, bodyTextStyle(18, '#cbb9e8'))
        .setOrigin(0, 0);

      const progress = song.difficultyCharts ? getBestProgress(song, saveManager) : saveManager.getSongProgress(song.id);
      this.add
        .text(x - cardW / 2 + 32, y + 100, `High Score: ${progress.highScore.toLocaleString()}`, bodyTextStyle(18, '#ffe066'))
        .setOrigin(0, 0);

      for (let s = 0; s < 3; s++) {
        const filled = s < progress.stars;
        this.add
          .star(x - cardW / 2 + 52 + s * 34, y + CARD_H - 28, 5, filled ? 12 : 10, filled ? 20 : 17, filled ? 0xffe066 : 0x554477)
          .setStrokeStyle(2, filled ? 0xffffff : 0x3a2a5e);
      }

      const playBtn = createButton(this, x + cardW / 2 - 100, y + CARD_H / 2, 160, 64, 'Play', PALETTE.primary, 26);
      playBtn.on('pointerup', () => {
        if (song.difficultyCharts) {
          this.showDifficultyModal(song, saveManager, audioManager);
        } else {
          audioManager.unlock();
          this.scene.start('GameplayScene', { songId: song.id });
        }
      });
    });

    const backBtn = createButton(this, 90, 70, 120, 60, 'Back', PALETTE.panelLight, 22);
    backBtn.on('pointerup', () => this.scene.start('HomeScene'));
  }

  /** A same-scene overlay (not a separate Phaser scene) since Song Select doesn't
   * need to keep running underneath -- just a dismissible panel on top of it. */
  private showDifficultyModal(song: SongMeta, saveManager: SaveManager, audioManager: AudioManager): void {
    const x = INTERNAL_WIDTH / 2;
    const panelW = 480;
    const rowH = 84;
    const panelH = 150 + DIFFICULTY_TIERS.length * rowH;
    const panelY = INTERNAL_HEIGHT / 2 - panelH / 2;
    const depth = 1000;

    const scrim = this.add.rectangle(0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT, 0x000000, 0.6).setOrigin(0).setDepth(depth);
    scrim.setInteractive();
    scrim.on('pointerup', () => this.closeDifficultyModal());

    const panel = this.add.graphics().setDepth(depth + 1);
    panel.fillStyle(PALETTE.panel, 1);
    panel.fillRoundedRect(x - panelW / 2, panelY, panelW, panelH, 28);
    panel.lineStyle(3, PALETTE.panelLight, 1);
    panel.strokeRoundedRect(x - panelW / 2, panelY, panelW, panelH, 28);

    const title = this.add
      .text(x, panelY + 44, 'Choose Difficulty', titleTextStyle(28))
      .setOrigin(0.5)
      .setDepth(depth + 2);
    const subtitle = this.add
      .text(x, panelY + 80, song.title, bodyTextStyle(18, '#cbb9e8'))
      .setOrigin(0.5)
      .setDepth(depth + 2);

    this.modalObjects.push(scrim, panel, title, subtitle);

    DIFFICULTY_TIERS.forEach((tier, i) => {
      const rowY = panelY + 132 + i * rowH + rowH / 2;
      const progress = saveManager.getSongProgress(`${song.id}:${tier.key}`);

      const btn = createButton(this, x, rowY, panelW - 64, 58, tier.label, tier.color, 24);
      btn.setDepth(depth + 2);
      btn.on('pointerup', () => {
        audioManager.unlock();
        this.scene.start('GameplayScene', { songId: song.id, difficulty: tier.key });
      });

      const info = this.add
        .text(x, rowY + 34, `Best: ${progress.highScore.toLocaleString()} • ${'★'.repeat(progress.stars)}${'☆'.repeat(3 - progress.stars)}`, bodyTextStyle(14, '#e9d9ff'))
        .setOrigin(0.5)
        .setDepth(depth + 2);

      this.modalObjects.push(btn, info);
    });
  }

  private closeDifficultyModal(): void {
    this.modalObjects.forEach((obj) => obj.destroy());
    this.modalObjects = [];
  }
}
